import React, { useEffect, useState } from 'react';
import { Smartphone } from 'lucide-react';

// URLs de las tiendas
const APP_STORE_URL = 'https://apps.apple.com/es/app/monkeybarber/id6756655968';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=club.monkeybarber.app';

type Platform = 'ios' | 'android' | 'desktop';

// Detectar plataforma inmediatamente (fuera del componente para ser más rápido)
const detectPlatform = (): Platform => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(userAgent)) return 'ios';
  if (/android/.test(userAgent)) return 'android';
  return 'desktop';
};

const DownloadAppPage: React.FC = () => {
  const [platform] = useState<Platform>(detectPlatform);

  // Redirección inmediata en móvil
  useEffect(() => {
    if (platform === 'ios') {
      window.location.replace(APP_STORE_URL);
    } else if (platform === 'android') {
      window.location.replace(PLAY_STORE_URL);
    }
  }, [platform]);

  // Solo mostrar UI en desktop
  if (platform !== 'desktop') {
    // Mientras redirige, mostrar pantalla mínima
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent/30 mx-auto mb-4"></div>
          <p className="text-ink">Abriendo tienda...</p>
          <a
            href={platform === 'ios' ? APP_STORE_URL : PLAY_STORE_URL}
            className="text-accent underline mt-2 inline-block"
          >
            Click aquí si no redirige
          </a>
        </div>
      </div>
    );
  }

  // Vista Desktop
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {/* Logo */}
        <div className="mb-8">
          <div className="w-24 h-24 mx-auto bg-accent/100 rounded-2xl flex items-center justify-center shadow-lg">
            <img
              src="/logo/logo_monkey.png"
              alt="MonkeyBarber"
              className="w-16 h-16 object-contain"
            />
          </div>
          <h1 className="mt-6 text-3xl font-bold text-ink">MonkeyBarber</h1>
          <p className="mt-2 text-ink-subtle">Tu barbería, sin esperas</p>
        </div>

        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
          <Smartphone className="w-16 h-16 mx-auto text-accent mb-6" />

          <h2 className="text-xl font-semibold text-ink mb-2">
            Descarga la App
          </h2>

          <p className="text-ink-subtle mb-8">
            Escanea el QR o elige tu tienda
          </p>

          {/* QR Code - apunta a Play Store directamente para Android */}
          <div className="bg-white p-4 rounded-xl inline-block mb-6">
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(PLAY_STORE_URL)}`}
              alt="QR Android"
              className="w-40 h-40"
            />
            <p className="text-xs text-ink-subtle mt-2">Android</p>
          </div>

          {/* Store Buttons */}
          <div className="space-y-3">
            <a
              href={APP_STORE_URL}
              className="w-full py-4 bg-white text-ink rounded-xl font-semibold flex items-center justify-center gap-3 hover:bg-surface-3 transition"
            >
              <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              App Store (iPhone)
            </a>

            <a
              href={PLAY_STORE_URL}
              className="w-full py-4 bg-success/100 text-ink rounded-xl font-semibold flex items-center justify-center gap-3 hover:bg-success/90 transition"
            >
              <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              Google Play (Android)
            </a>
          </div>
        </div>

        <p className="mt-8 text-ink-subtle text-sm">
          <a href="https://monkeybarber.club" className="hover:text-accent transition">
            monkeybarber.club
          </a>
        </p>
      </div>
    </div>
  );
};

export default DownloadAppPage;
